/**
 * EdgeFlyte Store — theme behaviors
 * Header collapse, mobile nav, search overlay, variant picker, AJAX cart drawer
 */
(function () {
  "use strict";

  const SECTION_CART = "cart-drawer";

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function debounce(fn, ms) {
    let t;
    return function () {
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, ms);
    };
  }

  /* —— Header scroll collapse —— */
  function initHeader() {
    const header = qs("[data-ef-header]");
    if (!header) return;

    const COLLAPSE_AT = 48;
    const EXPAND_AT = 12;
    let collapsed = window.scrollY > COLLAPSE_AT;
    if (collapsed) header.classList.add("is-scrolled", "is-collapsed");

    window.addEventListener(
      "scroll",
      function () {
        const y = window.scrollY;
        if (!collapsed && y > COLLAPSE_AT) {
          collapsed = true;
          header.classList.add("is-scrolled", "is-collapsed");
        } else if (collapsed && y < EXPAND_AT) {
          collapsed = false;
          header.classList.remove("is-scrolled", "is-collapsed");
        }
      },
      { passive: true }
    );

    const announce = qs("[data-ef-announce]", header);
    const dismissBtn = qs("[data-ef-announce-dismiss]", header);
    if (announce && dismissBtn) {
      const key = announce.getAttribute("data-ef-announce-id") || "ef-store-announce";
      const storageKey = "ef-announce-dismissed:" + key;
      if (sessionStorage.getItem(storageKey) === "1") {
        announce.hidden = true;
        const spacer = qs("[data-ef-header-spacer]");
        if (spacer) spacer.classList.remove("has-announce");
      }
      dismissBtn.addEventListener("click", function () {
        sessionStorage.setItem(storageKey, "1");
        announce.hidden = true;
        const spacer = qs("[data-ef-header-spacer]");
        if (spacer) spacer.classList.remove("has-announce");
      });
    }

    const menuToggle = qs("[data-ef-menu-toggle]", header);
    const mobilePanel = qs("[data-ef-mobile-panel]", header);
    if (menuToggle && mobilePanel) {
      menuToggle.addEventListener("click", function () {
        const open = mobilePanel.classList.toggle("is-open");
        menuToggle.setAttribute("aria-expanded", open ? "true" : "false");
        document.body.classList.toggle("ef-mobile-nav-open", open);
      });
    }
  }

  /* —— Search overlay —— */
  function initSearch() {
    const overlay = qs("[data-ef-search]");
    if (!overlay) return;
    const input = qs("input[type='search']", overlay);
    const openers = qsa("[data-ef-search-open]");
    const closers = qsa("[data-ef-search-close]", overlay);

    function open() {
      overlay.classList.add("is-open");
      document.body.classList.add("ef-search-open");
      if (input) setTimeout(function () { input.focus(); }, 50);
    }

    function close() {
      overlay.classList.remove("is-open");
      document.body.classList.remove("ef-search-open");
    }

    openers.forEach(function (btn) {
      btn.addEventListener("click", open);
    });
    closers.forEach(function (btn) {
      btn.addEventListener("click", close);
    });
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) close();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && overlay.classList.contains("is-open")) close();
    });
  }

  /* —— Accordion —— */
  function initAccordions() {
    qsa("[data-ef-accordion]").forEach(function (root) {
      qsa("[data-ef-accordion-trigger]", root).forEach(function (btn) {
        btn.addEventListener("click", function () {
          const item = btn.closest("[data-ef-accordion-item]");
          if (!item) return;
          const open = item.classList.toggle("is-open");
          btn.setAttribute("aria-expanded", open ? "true" : "false");
        });
      });
    });
  }

  /* —— Cart drawer —— */
  function getCartDrawer() {
    return qs("[data-ef-cart-drawer]");
  }

  function getCartOverlay() {
    return qs("[data-ef-cart-overlay]");
  }

  function openCartDrawer() {
    const drawer = getCartDrawer();
    const overlay = getCartOverlay();
    if (!drawer) {
      window.location.href = (window.themeRoutes && window.themeRoutes.cart) || "/cart";
      return;
    }
    drawer.classList.add("is-open");
    if (overlay) overlay.classList.add("is-open");
    document.body.classList.add("ef-drawer-open");
    drawer.setAttribute("aria-hidden", "false");
  }

  function closeCartDrawer() {
    const drawer = getCartDrawer();
    const overlay = getCartOverlay();
    if (drawer) {
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
    }
    if (overlay) overlay.classList.remove("is-open");
    document.body.classList.remove("ef-drawer-open");
  }

  function routesRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    if (window.themeRoutes && window.themeRoutes.root) {
      return window.themeRoutes.root;
    }
    return "/";
  }

  function updateCartCount(count) {
    const n = Number(count) || 0;
    qsa("[data-ef-cart-count]").forEach(function (el) {
      el.textContent = n > 0 ? String(n) : "";
      el.setAttribute("data-count", String(n));
      el.hidden = n <= 0;
      el.style.display = n > 0 ? "" : "none";
    });
    qsa("[data-ef-cart-count-value]").forEach(function (el) {
      el.textContent = String(n);
    });
    qsa("[data-ef-cart-open]").forEach(function (el) {
      el.setAttribute("aria-label", n > 0 ? "Open cart, " + n + " items" : "Open cart");
    });
  }

  function fetchCartJson() {
    return fetch(routesRoot() + "cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    }).then(function (res) {
      return res.json();
    });
  }

  function refreshCartCount() {
    return fetchCartJson().then(function (cart) {
      updateCartCount(cart.item_count);
      return cart;
    });
  }

  function replaceCartDrawerHtml(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const next = doc.querySelector("[data-ef-cart-drawer]");
    const current = getCartDrawer();
    if (next && current) {
      current.replaceWith(next);
      bindCartDrawerEvents();
    }
    const countEl = doc.querySelector("[data-ef-cart-count-value]");
    if (countEl) {
      updateCartCount(parseInt(countEl.textContent, 10) || 0);
    }
  }

  function fetchCartSection() {
    const url = routesRoot() + "?sections=" + SECTION_CART;
    return fetch(url, { credentials: "same-origin" })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (data && data[SECTION_CART]) {
          replaceCartDrawerHtml(data[SECTION_CART]);
        }
      })
      .catch(function () {
        /* Drawer HTML is optional; badge count is handled separately */
      });
  }

  function refreshCartUI() {
    return refreshCartCount().then(function () {
      return fetchCartSection();
    });
  }

  function addToCart(form) {
    const formData = new FormData(form);
    return fetch(routesRoot() + "cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      body: formData,
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw new Error(err.description || err.message || "Could not add to cart");
          });
        }
        return res.json();
      })
      .then(function () {
        return refreshCartUI();
      })
      .then(function () {
        openCartDrawer();
      });
  }

  function changeCartLine(key, quantity) {
    return fetch(routesRoot() + "cart/change.js", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        return fetchCartSection();
      });
  }

  function updateCartNote(note) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return fetch(root + "cart/update.js", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ note: note }),
    });
  }

  function bindCartDrawerEvents() {
    const drawer = getCartDrawer();
    const overlay = getCartOverlay();
    if (!drawer) return;

    qsa("[data-ef-cart-close]", drawer).forEach(function (btn) {
      btn.addEventListener("click", closeCartDrawer);
    });
    if (overlay) {
      overlay.addEventListener("click", closeCartDrawer);
    }

    qsa("[data-ef-cart-qty]", drawer).forEach(function (btn) {
      btn.addEventListener("click", function () {
        const key = btn.getAttribute("data-line-key");
        const delta = parseInt(btn.getAttribute("data-delta"), 10) || 0;
        const qtyEl = qs('[data-line-qty="' + key + '"]', drawer);
        const current = qtyEl ? parseInt(qtyEl.value, 10) || 0 : 0;
        const next = Math.max(0, current + delta);
        changeCartLine(key, next).then(function () {
          if (drawer.classList.contains("is-open")) openCartDrawer();
        });
      });
    });

    qsa("[data-ef-cart-remove]", drawer).forEach(function (btn) {
      btn.addEventListener("click", function () {
        const key = btn.getAttribute("data-line-key");
        changeCartLine(key, 0);
      });
    });

    const note = qs("[data-ef-cart-note]", drawer);
    if (note) {
      note.addEventListener(
        "change",
        debounce(function () {
          updateCartNote(note.value);
        }, 400)
      );
    }
  }

  function initCart() {
    qsa("[data-ef-cart-open]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        openCartDrawer();
      });
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeCartDrawer();
    });

    bindCartDrawerEvents();

    document.addEventListener("submit", function (e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!window.themeSettings || !window.themeSettings.cartDrawerEnabled) return;

      const isAjaxForm =
        form.hasAttribute("data-ef-product-form") || form.hasAttribute("data-ef-card-form");
      if (!isAjaxForm) return;

      // Let Shopify dynamic checkout buttons use native submit
      const submitter = e.submitter;
      if (
        submitter &&
        (submitter.closest(".shopify-payment-button") ||
          (submitter.classList && submitter.classList.contains("shopify-payment-button__button")))
      ) {
        return;
      }

      e.preventDefault();
      const submitBtn = form.querySelector('button[name="add"], [type="submit"][name="add"]');
      if (submitBtn) submitBtn.setAttribute("disabled", "disabled");
      addToCart(form)
        .catch(function (err) {
          console.error(err);
          alert(err.message || "Could not add to cart");
        })
        .finally(function () {
          if (submitBtn) submitBtn.removeAttribute("disabled");
        });
    });
  }

  /* —— Quantity steppers —— */
  function initQty() {
    document.addEventListener("click", function (e) {
      const btn = e.target.closest("[data-ef-qty]");
      if (!btn) return;
      const wrap = btn.closest("[data-ef-qty-wrap]");
      if (!wrap) return;
      const input = qs('input[type="number"]', wrap);
      if (!input) return;
      const delta = parseInt(btn.getAttribute("data-ef-qty"), 10) || 0;
      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 999;
      let val = (parseInt(input.value, 10) || min) + delta;
      val = Math.max(min, Math.min(max, val));
      input.value = String(val);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  /* —— Product gallery —— */
  var GALLERY_WIDTHS = [600, 900, 1200];

  function withImageWidth(src, width) {
    if (!src) return "";
    try {
      var url = new URL(src, window.location.origin);
      url.searchParams.set("width", String(width));
      if (/^https?:\/\//i.test(src)) return url.href;
      return url.pathname + url.search;
    } catch (err) {
      var sep = src.indexOf("?") >= 0 ? "&" : "?";
      return src + sep + "width=" + width;
    }
  }

  function buildSrcset(src) {
    if (!src) return "";
    return GALLERY_WIDTHS.map(function (w) {
      return withImageWidth(src, w) + " " + w + "w";
    }).join(", ");
  }

  function setGalleryMainImage(img, opts) {
    if (!img || !opts || !opts.src) return;
    var src = withImageWidth(opts.src, 1200);
    var srcset = opts.srcset || buildSrcset(opts.src);
    img.removeAttribute("srcset");
    img.src = src;
    if (srcset) img.setAttribute("srcset", srcset);
    img.sizes = opts.sizes || "(min-width: 900px) 50vw, 100vw";
    if (opts.alt != null) img.alt = opts.alt;
    if (opts.width) img.width = opts.width;
    if (opts.height) img.height = opts.height;
  }

  function activateGalleryThumb(gallery, imageId, src) {
    if (!gallery) return;
    var thumbs = qsa("[data-ef-gallery-thumb]", gallery);
    var matched = null;
    var srcPath = src ? String(src).split("?")[0] : "";
    thumbs.forEach(function (thumb) {
      thumb.classList.remove("is-active");
      var mediaId = thumb.getAttribute("data-media-id");
      var thumbImageId = thumb.getAttribute("data-image-id");
      var thumbSrc = thumb.getAttribute("data-src") || "";
      var thumbPath = thumbSrc.split("?")[0];
      if (
        (imageId && thumbImageId && String(thumbImageId) === String(imageId)) ||
        (imageId && mediaId && String(mediaId) === String(imageId)) ||
        (srcPath && thumbPath && (thumbPath === srcPath || srcPath.indexOf(thumbPath) >= 0 || thumbPath.indexOf(srcPath) >= 0))
      ) {
        matched = thumb;
      }
    });
    if (!matched && thumbs.length) matched = thumbs[0];
    if (matched) matched.classList.add("is-active");
  }

  function initGallery() {
    qsa("[data-ef-product-gallery]").forEach(function (gallery) {
      const main = qs("[data-ef-gallery-main]", gallery);
      const mainImg = main ? qs("img", main) : null;
      qsa("[data-ef-gallery-thumb]", gallery).forEach(function (thumb) {
        thumb.addEventListener("click", function () {
          const src = thumb.getAttribute("data-src");
          if (mainImg && src) {
            setGalleryMainImage(mainImg, {
              src: src,
              srcset: thumb.getAttribute("data-srcset") || "",
              alt: thumb.getAttribute("data-alt") || "",
              width: thumb.getAttribute("data-width"),
              height: thumb.getAttribute("data-height"),
            });
          }
          qsa("[data-ef-gallery-thumb]", gallery).forEach(function (t) {
            t.classList.remove("is-active");
          });
          thumb.classList.add("is-active");
        });
      });
    });
  }

  /* —— Variant picker —— */
  function initVariants() {
    qsa("[data-ef-product]").forEach(function (root) {
      const jsonEl = qs("[data-ef-product-json]", root);
      if (!jsonEl) return;
      let product;
      try {
        product = JSON.parse(jsonEl.textContent);
      } catch (err) {
        return;
      }

      const form = qs("[data-ef-product-form]", root);
      const idInput = form ? qs('input[name="id"]', form) : null;
      const priceEl = qs("[data-ef-product-price]", root);
      const availEl = qs("[data-ef-product-availability]", root);
      const skuEl = qs("[data-ef-product-sku]", root);
      const modelEl = qs("[data-ef-product-model]", root);
      const submitBtn = form ? qs('[type="submit"]', form) : null;
      const optionInputs = qsa("[data-ef-option]", root);

      function selectedOptions() {
        const opts = [];
        const groups = {};
        optionInputs.forEach(function (btn) {
          const name = btn.getAttribute("data-option-name");
          if (btn.classList.contains("is-active")) {
            groups[name] = btn.getAttribute("data-option-value");
          }
        });
        (product.options || []).forEach(function (optName) {
          opts.push(groups[optName] || "");
        });
        return opts;
      }

      function findVariant(opts) {
        return (product.variants || []).find(function (v) {
          return v.options.every(function (o, i) {
            return o === opts[i];
          });
        });
      }

      function formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
          return window.Shopify.formatMoney(cents, window.themeMoneyFormat || "${{amount}}");
        }
        return "$" + (cents / 100).toFixed(2);
      }

      function updateUI(variant) {
        if (!variant) {
          if (submitBtn) {
            submitBtn.setAttribute("disabled", "disabled");
            submitBtn.textContent = window.themeStrings && window.themeStrings.unavailable
              ? window.themeStrings.unavailable
              : "Unavailable";
          }
          if (availEl) {
            availEl.textContent = window.themeStrings && window.themeStrings.soldOut
              ? window.themeStrings.soldOut
              : "Sold out";
            availEl.classList.remove("is-available");
            availEl.classList.add("is-unavailable");
          }
          return;
        }

        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.innerHTML = formatMoney(variant.price);
        if (skuEl) skuEl.textContent = variant.sku || "";
        const mpnEl = qs("[data-ef-product-mpn]", root);
        if (mpnEl && !mpnEl.getAttribute("data-locked")) {
          mpnEl.textContent = variant.sku || mpnEl.textContent;
        }
        if (modelEl && variant.sku && !modelEl.getAttribute("data-locked")) {
          /* keep metafield model if locked */
        }

        if (variant.available) {
          if (submitBtn) {
            submitBtn.removeAttribute("disabled");
            submitBtn.textContent = window.themeStrings && window.themeStrings.addToCart
              ? window.themeStrings.addToCart
              : "Add to cart";
          }
          if (availEl) {
            availEl.textContent = window.themeStrings && window.themeStrings.inStock
              ? window.themeStrings.inStock
              : "In stock";
            availEl.classList.add("is-available");
            availEl.classList.remove("is-unavailable");
          }
        } else {
          if (submitBtn) {
            submitBtn.setAttribute("disabled", "disabled");
            submitBtn.textContent = window.themeStrings && window.themeStrings.soldOut
              ? window.themeStrings.soldOut
              : "Sold out";
          }
          if (availEl) {
            availEl.textContent = window.themeStrings && window.themeStrings.soldOut
              ? window.themeStrings.soldOut
              : "Sold out";
            availEl.classList.remove("is-available");
            availEl.classList.add("is-unavailable");
          }
        }

        if (variant.featured_image && variant.featured_image.src) {
          const gallery = qs("[data-ef-product-gallery]", root);
          const mainImg = qs("[data-ef-gallery-main] img", root);
          const imageId = variant.featured_image.id;
          let matchingThumb = null;
          if (gallery && imageId) {
            matchingThumb =
              qs('[data-ef-gallery-thumb][data-image-id="' + imageId + '"]', gallery) ||
              qs('[data-ef-gallery-thumb][data-media-id="' + imageId + '"]', gallery);
          }
          if (mainImg) {
            setGalleryMainImage(mainImg, {
              src: matchingThumb
                ? matchingThumb.getAttribute("data-src")
                : variant.featured_image.src,
              srcset: matchingThumb ? matchingThumb.getAttribute("data-srcset") || "" : "",
              alt: (matchingThumb && matchingThumb.getAttribute("data-alt")) || variant.featured_image.alt || product.title || "",
              width: (matchingThumb && matchingThumb.getAttribute("data-width")) || variant.featured_image.width,
              height: (matchingThumb && matchingThumb.getAttribute("data-height")) || variant.featured_image.height,
            });
          }
          activateGalleryThumb(gallery, imageId, variant.featured_image.src);
        }

        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url.toString());
      }

      optionInputs.forEach(function (btn) {
        btn.addEventListener("click", function () {
          if (btn.classList.contains("is-disabled")) return;
          const name = btn.getAttribute("data-option-name");
          qsa('[data-option-name="' + name + '"]', root).forEach(function (b) {
            b.classList.remove("is-active");
          });
          btn.classList.add("is-active");
          const variant = findVariant(selectedOptions());
          updateUI(variant);
        });
      });

      const current = findVariant(selectedOptions()) || product.variants[0];
      updateUI(current);
    });
  }

  /* —— Facet form auto-submit —— */
  function initFacets() {
    qsa("[data-ef-facets-form]").forEach(function (form) {
      qsa("input, select", form).forEach(function (el) {
        el.addEventListener("change", function () {
          form.submit();
        });
      });
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    initHeader();
    initSearch();
    initAccordions();
    initCart();
    initQty();
    initGallery();
    initVariants();
    initFacets();
  });

  window.EdgeFlyteTheme = {
    openCartDrawer: openCartDrawer,
    closeCartDrawer: closeCartDrawer,
    fetchCartSection: fetchCartSection,
  };
})();
